import React, { useState } from "react";
import {
  CHAT_AGENDA_EXTEND_LABEL,
  visibleAgendaItems,
} from "../../lib/chatAgenda";
import { formatHeuteScheduleMeta } from "../../lib/heuteScheduleBuckets";
import { formatSchoolAgendaMeta } from "../../lib/schoolAgendaDates";
import {
  SCHEDULE_DONE_UNDO_ACTION,
  SCHEDULE_DONE_UNDO_HINT,
  SCHEDULE_LABEL,
} from "../../lib/scheduleLabels";
import "./ChatAgendaCard.css";

function SchoolAgendaMeta({ item, line }) {
  const packLabel = String(item.packLabel || "").trim();
  if (!packLabel || !line.endsWith(packLabel)) return line;
  const lead = line.slice(0, line.length - packLabel.length);
  return (
    <>
      {lead}
      <strong className="chat-agenda-row-school">{packLabel}</strong>
    </>
  );
}

function AgendaRow({ item, onOpen, onToggleDone, showCalendarCue = false }) {
  const remind = !!item.remindEnabled;
  const calendarCue = showCalendarCue && !remind && !item.readOnly;
  const open = onOpen && !item.readOnly ? onOpen : null;
  const meta = item.readOnly
    ? formatSchoolAgendaMeta(item)
    : `${item.overdue ? "überfällig · " : ""}${formatHeuteScheduleMeta(item)}`;
  const inner = (
    <>
      <span className="chat-agenda-row-body">
        <span className="chat-agenda-row-title">{item.title}</span>
        <span className="chat-agenda-row-meta">
          {item.readOnly ? <SchoolAgendaMeta item={item} line={meta} /> : meta}
          {remind ? (
            <span className="chat-agenda-bell" title="Erinnerung an" aria-hidden="true">
              {" · "}🔔
            </span>
          ) : null}
        </span>
      </span>
      {calendarCue ? (
        <span
          className="chat-agenda-calendar"
          title="Kalender mit einem Tipp"
          aria-hidden="true"
        >
          📅
        </span>
      ) : null}
    </>
  );
  const className = `chat-agenda-row${item.overdue ? " chat-agenda-row--overdue" : ""}${
    open ? "" : " chat-agenda-row--static"
  }`;
  return (
    <li className={className}>
      {open ? (
        <button
          type="button"
          className="chat-agenda-row-main"
          onClick={() => open(item)}
          aria-label={`${item.title}, ${meta}${remind ? ", Erinnerung an" : ""}${calendarCue ? ", Kalender mit einem Tipp" : ""}`}
        >
          {inner}
        </button>
      ) : (
        <div className="chat-agenda-row-main">{inner}</div>
      )}
      {onToggleDone && item.id && !item.readOnly ? (
        <button
          type="button"
          className="chat-agenda-done"
          aria-label="Erledigt"
          title="Erledigt"
          onClick={() => onToggleDone(item)}
        >
          ✓
        </button>
      ) : null}
    </li>
  );
}

/**
 * Compact Termine & Fristen list inside a chat bubble.
 */
export default function ChatAgendaCard({
  items,
  loading = false,
  error = null,
  actionError = null,
  undo = null,
  onUndo,
  onOpen,
  onToggleDone,
  moreItems = [],
  showCalendarCue = false,
}) {
  const [extended, setExtended] = useState(false);
  const list = Array.isArray(items) ? items : [];
  const { visible, remaining } = visibleAgendaItems(list, moreItems, { extended });

  if (loading && visible.length === 0 && remaining === 0) return null;
  if (!loading && visible.length === 0 && remaining === 0 && !error && !undo) {
    return null;
  }

  return (
    <div className="chat-agenda" aria-label={SCHEDULE_LABEL}>
      {undo ? (
        <div className="chat-agenda-undo" role="status">
          <p className="chat-agenda-undo-text">
            {undo.hint || SCHEDULE_DONE_UNDO_HINT}
            {undo.title ? `: ${undo.title}` : ""}
          </p>
          <button type="button" className="chat-agenda-undo-btn" onClick={onUndo}>
            {SCHEDULE_DONE_UNDO_ACTION}
          </button>
        </div>
      ) : null}
      {error ? <p className="chat-agenda-error">{error}</p> : null}
      {actionError ? <p className="chat-agenda-error">{actionError}</p> : null}
      {visible.length > 0 ? (
        <ul className="chat-agenda-list">
          {visible.map((item) => (
            <AgendaRow
              key={item.key}
              item={item}
              onOpen={onOpen}
              onToggleDone={onToggleDone}
              showCalendarCue={showCalendarCue}
            />
          ))}
        </ul>
      ) : null}
      {remaining > 0 ? (
        <button
          type="button"
          className="chat-agenda-expand"
          onClick={() => setExtended(true)}
        >
          {CHAT_AGENDA_EXTEND_LABEL}
        </button>
      ) : null}
    </div>
  );
}
